import {
  Box,
  FormControl,
  FormLabel,
  Text,
  Textarea,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import CustomModal from 'components/modal/Modal';
import { useCreateEpisode } from 'hooks/useEpisodes';
import { usePatient } from 'hooks/usePatients';
import React, { useEffect, useState } from 'react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  patientId: string;
  existingEpisodeId?: string;
  initialStep?: number;
}

export default function EpisodeCreateModal({ isOpen, onClose, patientId }: Props) {
  const toast         = useToast();
  const { data: patient } = usePatient(patientId);
  const createEpisode = useCreateEpisode();

  const [motivo,   setMotivo]   = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const inputBg     = useColorModeValue('white', 'gray.800');
  const inputBorder = useColorModeValue('gray.200', 'gray.700');
  const nameColor   = useColorModeValue('gray.800', 'white');

  useEffect(() => {
    if (!isOpen) setMotivo('');
  }, [isOpen]);

  const handleCreate = async () => {
    if (!motivo.trim()) {
      toast({ title: 'Motivo de consulta requerido', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    setIsSaving(true);
    try {
      await createEpisode.mutateAsync({
        patientId,
        payload: { anamnesis: { motivo: motivo.trim() } },
      });
      toast({ title: 'Episodio creado', status: 'success', duration: 3000, isClosable: true });
      onClose();
    } catch (err: any) {
      const msg = err?.response?.status === 422
        ? 'El tarjetero del paciente no está activo'
        : err?.response?.data?.message ?? 'Error al crear episodio';
      toast({ title: 'Error', description: msg, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setIsSaving(false);
    }
  };

  const patientName = patient ? `${patient.nombres} ${patient.apellidos}` : '';

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      title="Nuevo Episodio Clínico"
      size="md"
      primaryBtnLabel="Crear Episodio"
      onPrimaryClick={handleCreate}
      primaryBtnLoading={isSaving}
    >
      <VStack spacing="18px" align="stretch">
        {patientName && (
          <Box pb="8px" borderBottom="1px solid" borderColor={inputBorder}>
            <Text fontSize="sm" fontWeight="600" color={nameColor}>
              {patientName}
            </Text>
          </Box>
        )}

        <FormControl isRequired>
          <FormLabel fontSize="sm" fontWeight="600">Motivo de Consulta</FormLabel>
          <Textarea
            placeholder="Describe el motivo principal de consulta..."
            rows={4}
            resize="none"
            fontSize="sm"
            value={motivo}
            onChange={e => setMotivo(e.target.value)}
            bg={inputBg}
            borderColor={inputBorder}
            autoFocus
          />
        </FormControl>
      </VStack>
    </CustomModal>
  );
}
